"use client";
import { Check, X } from "lucide-react";
import Link from "next/link";

import { AuthLink } from "@/components/shared/auth-link";

import { Button, Tag } from "@meetploy/storybook";

import type { Comparison, ComparisonValue } from "@/lib/comparisons";

function FeatureValue({ value }: { value: ComparisonValue }) {
	if (typeof value === "boolean") {
		return value ? (
			<Check className="h-5 w-5 text-green-600 dark:text-green-400" />
		) : (
			<X className="h-5 w-5 text-red-600 dark:text-red-400" />
		);
	}
	return (
		<span className="text-sm font-medium text-foreground text-center">
			{value}
		</span>
	);
}

export function ComparisonTable({ comparison }: { comparison: Comparison }) {
	return (
		<section className="w-full py-12 md:py-24 lg:py-32 bg-background">
			<div className="container px-4 md:px-6 max-w-5xl mx-auto">
				{comparison.angle && (
					<div className="mb-16 max-w-3xl mx-auto">
						<h2 className="text-2xl font-bold tracking-tight mb-4 text-foreground">
							{comparison.angle.title}
						</h2>
						<div className="space-y-4">
							{comparison.angle.paragraphs.map((paragraph) => (
								<p
									key={paragraph}
									className="text-muted-foreground leading-relaxed"
								>
									{paragraph}
								</p>
							))}
						</div>
					</div>
				)}

				<div className="text-center mb-12">
					<Tag className="mb-4">Compare platforms</Tag>
					<h2 className="text-3xl font-bold tracking-tight mb-2 text-foreground">
						Side by side
					</h2>
					<p className="text-muted-foreground">
						Compare Ploy and {comparison.competitor} feature by feature
					</p>
				</div>

				<div className="bg-card rounded-lg border border-border overflow-hidden shadow-sm">
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 sm:p-6 bg-muted/50 border-b border-border">
						<div className="hidden md:block" />
						<div className="text-center">
							<div className="border-2 border-primary rounded-lg p-4 bg-background shadow-sm h-full">
								<h3 className="font-bold text-lg mb-1 text-foreground">Ploy</h3>
								<p className="text-sm text-muted-foreground mb-2 uppercase">
									Open source · Batteries included
								</p>
								<p className="text-2xl font-bold text-primary">From $0</p>
								<p className="text-xs text-muted-foreground mt-1">
									Self-host free forever
								</p>
							</div>
						</div>
						<div className="text-center">
							<div className="border border-border rounded-lg p-4 bg-background h-full">
								<h3 className="font-bold text-lg mb-1 text-foreground">
									{comparison.competitor}
								</h3>
								<p className="text-sm text-muted-foreground mb-2 uppercase">
									{comparison.competitorLabel}
								</p>
								<p className="text-2xl font-bold text-foreground">
									{comparison.competitorPrice}
								</p>
								<p className="text-xs text-muted-foreground mt-1">
									{comparison.competitorPriceNote}
								</p>
							</div>
						</div>
					</div>

					{comparison.categories.map((category) => (
						<div key={category.category}>
							<div className="px-6 py-3 bg-muted/30 border-b border-border/50 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
								{category.category}
							</div>
							{category.rows.map((row) => (
								<div
									key={row.title}
									className="grid grid-cols-3 gap-4 p-6 border-b border-border/50 hover:bg-muted/30 transition-colors"
								>
									<div>
										<h4 className="font-semibold text-foreground mb-1">
											{row.title}
										</h4>
										<p className="text-sm text-muted-foreground">
											{row.description}
										</p>
									</div>
									<div className="flex justify-center items-center">
										<FeatureValue value={row.ploy} />
									</div>
									<div className="flex justify-center items-center">
										<FeatureValue value={row.competitor} />
									</div>
								</div>
							))}
						</div>
					))}
				</div>

				<div className="mt-8 bg-primary/5 dark:bg-primary/10 rounded-lg p-6 border border-primary/20">
					<h3 className="font-bold text-lg mb-3 text-primary">
						Why choose Ploy?
					</h3>
					<div className="grid md:grid-cols-2 gap-4 text-sm">
						{comparison.reasons.map((reason) => (
							<div key={reason.title} className="flex items-start gap-2">
								<Check className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
								<span className="text-foreground">
									<strong>{reason.title}</strong>: {reason.body}
								</span>
							</div>
						))}
					</div>
				</div>

				<div className="mt-12 grid md:grid-cols-2 gap-6">
					<div className="rounded-lg border border-border bg-card p-6">
						<h3 className="font-bold text-lg mb-4 text-foreground">
							Choose Ploy if
						</h3>
						<ul className="space-y-3 text-sm text-muted-foreground">
							{comparison.fit.ploy.map((item) => (
								<li key={item} className="flex items-start gap-2">
									<Check className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
									<span>{item}</span>
								</li>
							))}
						</ul>
					</div>
					<div className="rounded-lg border border-border bg-card p-6">
						<h3 className="font-bold text-lg mb-4 text-foreground">
							Choose {comparison.competitor} if
						</h3>
						<ul className="space-y-3 text-sm text-muted-foreground">
							{comparison.fit.competitor.map((item) => (
								<li key={item} className="flex items-start gap-2">
									<span
										aria-hidden
										className="mt-2 h-1.5 w-1.5 rounded-full bg-muted-foreground/60 flex-shrink-0"
									/>
									<span>{item}</span>
								</li>
							))}
						</ul>
					</div>
				</div>

				{comparison.faq.length > 0 && (
					<div className="mt-16 max-w-3xl mx-auto">
						<h2 className="text-2xl font-bold tracking-tight mb-6 text-foreground">
							Frequently asked questions
						</h2>
						<dl className="space-y-6">
							{comparison.faq.map((item) => (
								<div key={item.question}>
									<dt className="font-semibold text-foreground mb-2">
										{item.question}
									</dt>
									<dd className="text-muted-foreground leading-relaxed">
										{item.answer}
									</dd>
								</div>
							))}
						</dl>
					</div>
				)}

				<div className="text-center mt-16">
					<div className="flex flex-col sm:flex-row gap-4 justify-center">
						<Button size="lg" render={<AuthLink href="/signup" />}>
							Deploy for free
						</Button>
						<Button
							size="lg"
							variant="outline"
							render={<Link prefetch={true} href="/compare" />}
						>
							All comparisons
						</Button>
					</div>
					<p className="text-sm text-muted-foreground mt-3">
						No credit card required • Self-host option available • Open source
						under AGPLv3
					</p>
				</div>
			</div>
		</section>
	);
}
